/**
 * Expand/collapse block for the stats panel's sections (user request
 * 2026-10-21): a header row - caption left, a one-line summary and a
 * chevron right - that shows or hides the body under it. Used by the
 * Source (FtgsSourceBadges), Advanced (FtgsStereoControls) and
 * Performance (FtgsStatsOverlay) sections, so the three behave alike.
 *
 * Every block starts collapsed; its open/closed state is remembered per
 * browser under its own localStorage key. Storage is only a convenience -
 * a blocked or empty store just means collapsed.
 *
 * The header borrows the stats panel's row look (`.ftgs-stats-row` +
 * `.ftgs-stats-toggle`, see ftgs-stats-overlay.scss), so it must live inside
 * #ftgs-stats-overlay. The body is indented one step there
 * (`.ftgs-collapsible-body`).
 */

const readStoredOpen = (key: string): boolean => {
    try {
        return localStorage.getItem(key) === '1';
    } catch {
        return false;
    }
};

const writeStoredOpen = (key: string, open: boolean) => {
    try {
        localStorage.setItem(key, open ? '1' : '0');
    } catch {
        // storage blocked (private window etc.) - just not remembered
    }
};

class FtgsCollapsible {
    readonly header: HTMLDivElement;
    readonly body: HTMLDivElement;
    /** right-hand text on the header - what the section is set to, readable while collapsed */
    readonly summary: HTMLSpanElement;
    private chevron: HTMLSpanElement;
    private storageKey: string;

    constructor(caption: string, storageKey: string, bodyClass?: string) {
        this.storageKey = storageKey;

        this.header = document.createElement('div');
        this.header.className = 'ftgs-stats-row ftgs-stats-toggle';
        this.header.setAttribute('role', 'button');
        const label = document.createElement('span');
        label.className = 'ftgs-stats-label';
        label.textContent = caption;
        this.header.appendChild(label);
        const right = document.createElement('span');
        right.className = 'ftgs-stats-toggle-right';
        this.summary = document.createElement('span');
        this.summary.className = 'ftgs-stats-value';
        this.chevron = document.createElement('span');
        this.chevron.className = 'ftgs-stats-chevron';
        right.append(this.summary, this.chevron);
        this.header.appendChild(right);
        this.header.addEventListener('click', () => this.setOpen(!this.open));

        this.body = document.createElement('div');
        this.body.className = bodyClass ? `ftgs-collapsible-body ${bodyClass}` : 'ftgs-collapsible-body';

        this.setOpen(readStoredOpen(storageKey), false);
    }

    get open(): boolean {
        return this.body.style.display !== 'none';
    }

    /** expand or collapse (remembered unless `remember` is false) */
    setOpen(open: boolean, remember = true) {
        this.body.style.display = open ? '' : 'none';
        this.chevron.textContent = open ? '▾' : '▸';
        this.header.setAttribute('aria-expanded', String(open));
        if (remember) {
            writeStoredOpen(this.storageKey, open);
        }
    }

    /** header then body, into `parent` */
    appendTo(parent: HTMLElement) {
        parent.append(this.header, this.body);
    }
}

export { FtgsCollapsible };
